import IconButton from 'components/@extended/IconButton';
import { Close as CloseIcon } from 'components/icons';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from 'components/ui/combobox';
import { Input } from 'components/ui/input';
import { useId, useState } from 'react';
import { JobRoleDto } from 'types/jobRoles';
import { PersonalRateDraft } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { HourlyRate } from 'types/pricelists';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { PersonalRateStatus, personalRateStatus } from './matterPricelistEditorUtils';

// Someone the ledger may price: an active employee, with the role they hold today.
export interface RateCandidate extends NamedEntityMinDto {
    jobRole: JobRoleDto | null;
}

interface Props {
    rows: PersonalRateDraft[];
    // Every active employee, undefined until loaded.
    activeEmployees: RateCandidate[] | undefined;
    caseTeamIds: Set<number>;
    roleRates: HourlyRate[];
    currencyTypeCode?: string | null;
    isValidating?: boolean;
    // An employee-priced case: the rows are the list's whole pricing, grouped by the role each person holds today,
    // the role a plain label with no role rate beside it.
    perEmployee?: boolean;
    onChange: (rows: PersonalRateDraft[]) => void;
}

interface LedgerGroup {
    key: string;
    title: string | null;
    rows: PersonalRateDraft[];
}

const PRICE_PATTERN = new RegExp('^[0-9]*[.,]?([0-9]{0,2})?$');

const isPriced = (price: string | null): boolean => price !== null && price !== '';

function roleName(jobRole: JobRoleDto | null): string | null {
    return jobRole ? jobRole.internalName || jobRole.name : null;
}

// In the job roles' own order, people without a role last; a person keeps their place within the group.
function groupByRole(rows: PersonalRateDraft[]): LedgerGroup[] {
    const groups = new Map<string, LedgerGroup & { order: number }>();
    rows.forEach((row) => {
        const key = row.jobRole ? `role-${row.jobRole.id}` : 'no-role';
        const group = groups.get(key) ?? {
            key,
            title: roleName(row.jobRole) ?? t('pricelists.employee.no-role'),
            rows: [],
            order: row.jobRole ? (row.jobRole.orderNum ?? Number.MAX_SAFE_INTEGER) : Infinity,
        };
        group.rows.push(row);
        groups.set(key, group);
    });
    return [...groups.values()]
        .toSorted((a, b) => a.order - b.order || (a.title ?? '').localeCompare(b.title ?? ''))
        .map(({ key, title, rows: groupRows }) => ({ key, title, rows: groupRows }));
}

const draftFor = (person: RateCandidate): PersonalRateDraft => ({
    employee: { id: person.id, name: person.name },
    jobRole: person.jobRole,
    hourlyPrice: '',
});

function statusLabel(status: PersonalRateStatus): string {
    return status === 'NO_LONGER_ACTIVE'
        ? t('pricelists.case-editor.no-longer-active')
        : t('pricelists.case-editor.not-on-case-team');
}

/**
 * The individual-rate rows of one list, under the role grid: each names a person, the role rate they would
 * otherwise bill, and the price they carry instead — all on one line, so the comparison survives the
 * override. Any active employee is offered, once per list; a row whose person is off the case team or no
 * longer active keeps working and says so.
 */
export const PersonalRatesLedger = ({
    rows,
    activeEmployees,
    caseTeamIds,
    roleRates,
    currencyTypeCode,
    isValidating,
    perEmployee = false,
    onChange,
}: Props): JSX.Element => {
    const [isAdding, setIsAdding] = useState(false);
    const caseTeamReasonId = useId();
    const pricedIds = new Set(rows.map((row) => row.employee.id));
    const activeEmployeeIds = activeEmployees ? new Set(activeEmployees.map((person) => person.id)) : null;
    const activeCaseTeam = activeEmployees?.filter((person) => caseTeamIds.has(person.id));
    const unlistedCaseTeam = activeCaseTeam?.filter((person) => !pricedIds.has(person.id)) ?? [];
    let caseTeamOffReason: string | null = null;
    if (activeCaseTeam?.length === 0) {
        caseTeamOffReason = t('pricelists.case-editor.add-case-team-no-team');
    } else if (activeCaseTeam && unlistedCaseTeam.length === 0) {
        caseTeamOffReason = t('pricelists.case-editor.add-case-team-all-listed');
    }

    const roleRate = (jobRole: JobRoleDto | null): string | null =>
        jobRole ? (roleRates.find((rate) => rate.jobRole.id === jobRole.id)?.hourlyPrice ?? null) : null;

    const money = (rate: string): string => `${currencyTypeCode ?? ''} ${rate}`.trim();

    // "Partner · role rate EUR 150", or the note that they hold no role at all — the ledger is then the only
    // place their price can come from — prefixed when the person is off the case team or no longer active.
    const subtitle = (row: PersonalRateDraft): string => {
        const status = personalRateStatus(row.employee.id, caseTeamIds, activeEmployeeIds);
        if (perEmployee) {
            return status ? statusLabel(status) : '';
        }
        const role = roleName(row.jobRole);
        const rate = roleRate(row.jobRole);
        const detail = !role
            ? t('pricelists.case-editor.no-role-rate-required')
            : isPriced(rate)
              ? t('pricelists.case-editor.role-rate', { role, rate: money(rate as string) })
              : role;
        return status
            ? t('pricelists.case-editor.status-with-detail', { status: statusLabel(status), detail })
            : detail;
    };

    const optionSubtitle = (person: RateCandidate): string => {
        if (pricedIds.has(person.id)) {
            return t('pricelists.case-editor.already-in-ledger');
        }
        const role = roleName(person.jobRole);
        if (perEmployee) {
            return role ?? t('pricelists.employee.no-role');
        }
        if (!role) {
            return t('pricelists.case-editor.no-role-rate-will-be-required');
        }
        const rate = roleRate(person.jobRole);
        return isPriced(rate)
            ? t('pricelists.case-editor.would-bill-role-rate', { role, rate: money(rate as string) })
            : role;
    };

    const count = rows.length;
    const groups: LedgerGroup[] = perEmployee ? groupByRole(rows) : [{ key: 'all', title: null, rows }];

    return (
        <div className="flex flex-col">
            <div className="flex items-baseline gap-2 pb-1.5">
                <span className="new:text-label new:tracking-[0.06em] text-[0.7rem] font-semibold tracking-wider uppercase">
                    {t(perEmployee ? 'pricelists.case-editor.colleagues' : 'pricelists.case-editor.individual-rates')}
                </span>
                {count > 0 ? (
                    <span className="text-grey-500 new:text-caption text-[0.65rem]">
                        {count === 1
                            ? t('pricelists.case-editor.ledger-count-one')
                            : t('pricelists.case-editor.ledger-count', { count: String(count) })}
                    </span>
                ) : null}
            </div>

            {count === 0 ? (
                <div className="text-grey-500 new:text-caption pt-0.5 pb-1 text-[0.7rem]">
                    {t(perEmployee ? 'pricelists.case-editor.colleagues-empty' : 'pricelists.case-editor.ledger-empty')}
                </div>
            ) : null}

            {groups.map((group) => (
                <div key={group.key} className="flex flex-col">
                    {group.title ? (
                        <div className="text-grey-600 new:text-caption pt-1.5 pb-0.5 text-[0.7rem] font-semibold">
                            {group.title}
                        </div>
                    ) : null}
                    {group.rows.map((row) => {
                        const isError = !!isValidating && !isPriced(row.hourlyPrice);
                        return (
                            <div key={row.employee.id} className="flex items-center gap-2 py-0.5">
                                <span className={cn('flex-1 truncate text-[0.8rem]', isError && 'text-red-600')}>
                                    {row.employee.name}
                                    <span className="text-red-600"> *</span>
                                    <span
                                        className={cn(
                                            'text-grey-500 new:text-caption ml-2 text-[0.65rem]',
                                            isError && 'text-red-600'
                                        )}
                                    >
                                        {subtitle(row)}
                                        {isError ? t('pricelists.case-editor.price-required-suffix') : ''}
                                    </span>
                                </span>
                                <Input
                                    aria-label={row.employee.name}
                                    aria-invalid={isError}
                                    className="bg-paper w-18 flex-none text-right text-base tabular-nums md:text-xs"
                                    value={row.hourlyPrice ?? ''}
                                    onChange={(event) => {
                                        if (PRICE_PATTERN.test(event.target.value)) {
                                            const value = event.target.value.replace(',', '.');
                                            onChange(
                                                rows.map((candidate) =>
                                                    candidate.employee.id === row.employee.id
                                                        ? { ...candidate, hourlyPrice: value }
                                                        : candidate
                                                )
                                            );
                                        }
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    className="text-fg-secondary hover:text-fg size-7.5 flex-none"
                                    aria-label={t('remove')}
                                    onClick={() =>
                                        onChange(rows.filter((candidate) => candidate.employee.id !== row.employee.id))
                                    }
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </div>
                        );
                    })}
                </div>
            ))}

            {isAdding ? (
                <Combobox
                    items={activeEmployees ?? []}
                    value={null}
                    defaultOpen
                    itemToStringLabel={(person: RateCandidate) => person.name}
                    isItemEqualToValue={(option: RateCandidate, value: RateCandidate) => option.id === value.id}
                    // A raw onBlur would race the option click and swallow the pick; the close reason
                    // separates giving up on the search from choosing someone out of it.
                    onOpenChange={(open, { reason }) => {
                        if (
                            !open &&
                            (reason === 'focus-out' || reason === 'outside-press' || reason === 'escape-key')
                        ) {
                            setIsAdding(false);
                        }
                    }}
                    onValueChange={(person: RateCandidate | null) => {
                        setIsAdding(false);
                        if (person && !pricedIds.has(person.id)) {
                            onChange([...rows, draftFor(person)]);
                        }
                    }}
                >
                    <ComboboxInput
                        autoFocus
                        placeholder={t('pricelists.case-editor.add-person')}
                        aria-label={t('pricelists.case-editor.add-person')}
                        className="bg-paper mt-2 w-full max-w-96 [&_input]:text-base md:[&_input]:text-xs"
                    />
                    <ComboboxContent>
                        <ComboboxEmpty>{t('no-options')}</ComboboxEmpty>
                        <ComboboxList>
                            {(person: RateCandidate) => (
                                <ComboboxItem key={person.id} value={person} disabled={pricedIds.has(person.id)}>
                                    <div className="flex flex-col">
                                        <span className="text-[0.8rem]">{person.name}</span>
                                        <span className="text-grey-500 new:text-caption text-[0.65rem]">
                                            {optionSubtitle(person)}
                                        </span>
                                    </div>
                                </ComboboxItem>
                            )}
                        </ComboboxList>
                    </ComboboxContent>
                </Combobox>
            ) : (
                <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <button
                        type="button"
                        className="text-link new:text-caption cursor-pointer border-none bg-inherit p-0 text-[0.72rem]"
                        onClick={() => setIsAdding(true)}
                    >
                        {t(
                            perEmployee
                                ? 'pricelists.case-editor.add-colleague'
                                : 'pricelists.case-editor.add-individual-rate'
                        )}
                    </button>
                    {perEmployee ? (
                        <>
                            <button
                                type="button"
                                className="text-link new:text-caption aria-disabled:text-grey-500 cursor-pointer border-none bg-inherit p-0 text-[0.72rem] aria-disabled:cursor-default"
                                aria-disabled={unlistedCaseTeam.length === 0 || undefined}
                                aria-describedby={caseTeamOffReason ? caseTeamReasonId : undefined}
                                onClick={() => {
                                    if (unlistedCaseTeam.length > 0) {
                                        onChange([...rows, ...unlistedCaseTeam.map(draftFor)]);
                                    }
                                }}
                            >
                                {t('pricelists.case-editor.add-case-team')}
                            </button>
                            {caseTeamOffReason ? (
                                <span id={caseTeamReasonId} className="text-grey-500 new:text-caption text-[0.65rem]">
                                    {caseTeamOffReason}
                                </span>
                            ) : null}
                        </>
                    ) : null}
                </div>
            )}

            {count > 0 ? (
                <div className="text-grey-500 new:text-caption pt-2 text-[0.65rem]">
                    {t('pricelists.case-editor.ledger-caption')}
                </div>
            ) : null}
        </div>
    );
};
